import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "@/components/marketing/BrandMark";
import { api } from "@/lib/api";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—");

const STATUS_LABELS = {
  trial: "Free trial", active: "Active", past_due: "Past due",
  cancelled: "Cancelled", expired: "Trial expired", suspended: "Suspended",
};

const Subscribe = () => {
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    api("/billing/status").then((d) => setBilling(d.billing)).catch(() => {});
  }, []);

  const subscribe = async () => {
    setBusy(true);
    setNotice("");
    try {
      await api("/billing/subscribe", { method: "POST" });
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] font-sans" data-testid="subscribe-page">
      <header className="border-b border-guinep-line/50 bg-[#F7F7F5]/90">
        <div className="mx-auto flex max-w-md items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-guinep-soft"><BrandMark size={14} /></span>
            <span className="font-serif text-base font-semibold tracking-tight text-guinep-ink">Guinep Seed</span>
          </div>
          <Link to="/app" data-testid="subscribe-back" className="inline-flex items-center gap-1.5 text-xs font-medium text-guinep-mute transition-colors hover:text-guinep-ink">
            <ArrowLeft size={13} /> Dashboard
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-md px-5 py-10">
        <h1 className="font-serif text-3xl font-medium tracking-tight text-guinep-ink">Keep your Guinep Seed page live</h1>

        {billing && (
          <p className="mt-3 text-sm text-guinep-mute" data-testid="subscribe-status-line">
            {billing.status === "trial" && `You have ${billing.trial_days_remaining} ${billing.trial_days_remaining === 1 ? "day" : "days"} left in your free trial.`}
            {billing.status === "expired" && "Your free trial has ended."}
            {billing.status === "active" && "You're subscribed. Your Guinep Seed page will stay live."}
            {billing.status === "past_due" && "We couldn't process your subscription payment."}
            {billing.status === "cancelled" && `Your access continues until ${fmtDate(billing.current_period_end)}.`}
          </p>
        )}

        <div className="mt-7 rounded-2xl border border-guinep-line bg-white p-6" data-testid="subscribe-pricing-card">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-guinep-mid">One simple plan</p>
          <p className="mt-4 font-serif text-4xl font-medium text-guinep-ink">14 days free</p>
          <div className="mt-3 space-y-1.5">
            <p className="text-sm text-guinep-ink">Then <span className="font-semibold">$9</span> for your first month</p>
            <p className="text-sm text-guinep-ink"><span className="font-semibold">$12/month</span> after that</p>
          </div>
          <p className="mt-3 text-xs text-guinep-mute">No commission. No transaction fees. Cancel anytime.</p>

          <ul className="mt-5 space-y-2 border-t border-guinep-line/60 pt-5">
            {["Your public Guinep Seed page", "Services, prices, Now updates and policies", "Structured enquiries into WhatsApp", "Share Kit", "Simple activity insights"].map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-[13px] text-guinep-ink">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-guinep-soft text-guinep-mid"><Check size={10} strokeWidth={3} /></span>
                {f}
              </li>
            ))}
          </ul>

          {billing?.status === "trial" && (
            <p className="mt-5 rounded-xl bg-guinep-soft px-4 py-3 text-[13px] leading-relaxed text-guinep-deep">
              After your trial, your first month is $9. After that, your subscription renews at $12/month.
            </p>
          )}

          {billing?.status === "active" ? (
            <div className="mt-6 rounded-xl bg-guinep-soft px-4 py-3.5 text-center">
              <p className="text-sm font-semibold text-guinep-deep">You're subscribed.</p>
              <p className="mt-0.5 text-xs text-guinep-mute">Your Guinep Seed page will stay live.</p>
            </div>
          ) : (
            <button
              data-testid="subscribe-button"
              onClick={subscribe}
              disabled={busy}
              className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-guinep-deep py-3.5 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid disabled:opacity-50"
            >
              {busy ? "Please wait…" : billing?.status === "past_due" ? "Update payment" : "Subscribe — $9 first month"}
            </button>
          )}

          {notice && (
            <p className="mt-4 flex items-start gap-2 rounded-xl border border-guinep-line bg-guinep-soft/60 px-4 py-3 text-[13px] leading-relaxed text-guinep-deep" data-testid="subscribe-notice">
              <Info size={14} className="mt-0.5 shrink-0" /> {notice}
            </p>
          )}
        </div>

        {billing && billing.status !== "trial" && billing.status !== "expired" && (
          <p className="mt-5 text-center text-xs text-guinep-mute">
            Status: <span className="font-semibold text-guinep-ink">{STATUS_LABELS[billing.status]}</span>
            {billing.next_billing_date && billing.next_amount && <> · Next billing {fmtDate(billing.next_billing_date)} (${billing.next_amount})</>}
          </p>
        )}
      </main>
    </div>
  );
};

export default Subscribe;
